(() => {
  'use strict';

  const normalize = (value) => (value || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('sv-SE')
    .trim();

  const init = () => {
    const grid = document.querySelector('[data-pax-main-collection-grid]');
    if (!grid) return;

    const discovery = document.querySelector('[data-pax-collection-discovery]');
    const queryInput = discovery && discovery.querySelector('[data-pax-collection-query]');
    const clearButton = discovery && discovery.querySelector('[data-pax-collection-clear]');
    const brandList = discovery && discovery.querySelector('[data-pax-brand-list]');
    const status = discovery && discovery.parentElement.querySelector('[data-pax-local-filter-status]');

    let pagination = document.querySelector('[data-pax-infinite-pagination]');
    let nextLink = pagination && pagination.querySelector('.pax-infinite-next');
    let sentinel = pagination && pagination.querySelector('[data-pax-infinite-sentinel]');
    const infiniteStatus = pagination && pagination.querySelector('[data-pax-infinite-status]');

    const absoluteUrl = (href) => href ? new URL(href, window.location.href).href : null;
    const initialNextUrl = nextLink ? absoluteUrl(nextLink.getAttribute('href')) : null;
    let visibleNextUrl = initialNextUrl;
    let visibleLoading = false;
    let selectedVendor = '';
    let filterActive = false;
    let filterRevision = 0;
    let debounceTimer = null;
    let resultGrid = null;

    const pagePromises = new Map();
    const pageResults = new Map();

    const collectionItems = (container) => Array.from(container ? container.children : [])
      .filter((node) => node.classList && node.classList.contains('pax-collection-item'));

    const parsePage = (html, url) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextGrid = doc.querySelector('[data-pax-main-collection-grid]');
      if (!nextGrid) throw new Error('Produktgrid saknas på ' + url);
      const items = collectionItems(nextGrid);
      const nextPagination = doc.querySelector('[data-pax-infinite-pagination]');
      const following = nextPagination && nextPagination.querySelector('.pax-infinite-next');
      return { items, nextUrl: following ? absoluteUrl(following.getAttribute('href')) : null };
    };

    const fetchPage = (url) => {
      if (!url) return Promise.resolve({ items: [], nextUrl: null });
      if (pagePromises.has(url)) return pagePromises.get(url);
      const promise = fetch(url, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then((response) => {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then((html) => {
          const result = parsePage(html, url);
          pageResults.set(url, result);
          return result;
        })
        .catch((error) => {
          pagePromises.delete(url);
          throw error;
        });
      pagePromises.set(url, promise);
      return promise;
    };

    let scanPromise = null;
    const scanAllPages = () => {
      if (!initialNextUrl) return Promise.resolve();
      if (scanPromise) return scanPromise;
      scanPromise = (async () => {
        let url = initialNextUrl;
        const seen = new Set();
        while (url && !seen.has(url)) {
          seen.add(url);
          const result = await fetchPage(url);
          url = result.nextUrl;
        }
      })().catch((error) => {
        scanPromise = null;
        throw error;
      });
      return scanPromise;
    };

    const appendResult = (result) => {
      let count = 0;
      result.items.forEach((item) => {
        if (!item.isConnected) {
          grid.appendChild(item);
          count += 1;
        }
      });
      if (count) {
        document.dispatchEvent(new CustomEvent('pax:products-appended', { detail: { count } }));
      }
      return count;
    };

    let observer = null;
    const finishPagination = () => {
      visibleNextUrl = null;
      if (observer) observer.disconnect();
      if (pagination && pagination.isConnected) pagination.remove();
      pagination = null;
      nextLink = null;
      sentinel = null;
    };

    const loadVisibleNext = async () => {
      if (!visibleNextUrl || visibleLoading || filterActive) return;
      visibleLoading = true;
      document.documentElement.classList.add('pax-infinite-loading');
      if (infiniteStatus) infiniteStatus.textContent = 'Laddar fler produkter…';
      try {
        const result = await fetchPage(visibleNextUrl);
        const count = appendResult(result);
        visibleNextUrl = result.nextUrl;
        if (visibleNextUrl && nextLink) {
          nextLink.href = visibleNextUrl;
          if (infiniteStatus) infiniteStatus.textContent = count + ' fler produkter laddades.';
        } else {
          finishPagination();
        }
      } catch (error) {
        console.error('PAX infinite scroll:', error);
        document.documentElement.classList.remove('pax-infinite-ready');
        if (infiniteStatus) {
          infiniteStatus.textContent = 'Automatisk laddning misslyckades. Använd knappen för att ladda fler produkter.';
        }
        if (observer) observer.disconnect();
      } finally {
        visibleLoading = false;
        document.documentElement.classList.remove('pax-infinite-loading');
      }
    };

    if (nextLink && sentinel && 'fetch' in window && 'IntersectionObserver' in window) {
      document.documentElement.classList.add('pax-infinite-ready');
      observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadVisibleNext();
      }, { rootMargin: '900px 0px' });
      observer.observe(sentinel);
      nextLink.addEventListener('click', (event) => {
        if (!document.documentElement.classList.contains('pax-infinite-ready')) return;
        event.preventDefault();
        loadVisibleNext();
      });
    }

    const allKnownItems = () => {
      const set = new Set(collectionItems(grid));
      pageResults.forEach((result) => result.items.forEach((item) => set.add(item)));
      return Array.from(set);
    };

    const setBrandActiveState = () => {
      if (!brandList) return;
      brandList.querySelectorAll('[data-pax-vendor]').forEach((button) => {
        button.classList.toggle('is-active', (button.dataset.paxVendor || '') === selectedVendor);
      });
    };

    const showNormalGrid = () => {
      filterActive = false;
      if (resultGrid) {
        resultGrid.remove();
        resultGrid = null;
      }
      grid.hidden = false;
      if (pagination && pagination.isConnected) pagination.style.display = '';
      if (status) {
        status.textContent = '';
        status.classList.remove('is-loading');
      }
      if (clearButton) clearButton.hidden = true;
    };

    const renderFilteredGrid = (items, query, vendorKey, pending) => {
      if (!resultGrid) {
        resultGrid = document.createElement('div');
        resultGrid.className = grid.className + ' pax-local-results-grid';
        resultGrid.style.cssText = grid.style.cssText;
        grid.parentNode.insertBefore(resultGrid, grid);
      }

      resultGrid.innerHTML = '';
      let shown = 0;

      items.forEach((item) => {
        const haystack = normalize(item.dataset.paxSearch);
        const itemVendor = normalize(item.dataset.paxVendor);
        const matchesQuery = !query || haystack.includes(query);
        const matchesVendor = !vendorKey || itemVendor === vendorKey;
        if (matchesQuery && matchesVendor) {
          resultGrid.appendChild(item.cloneNode(true));
          shown += 1;
        }
      });

      if (!shown) {
        const message = document.createElement('div');
        message.className = 'pax-collection-no-results';
        if (pending) {
          message.innerHTML = '<strong>Söker i hela kollektionen…</strong><span>Kontrollerar fler produkter.</span>';
        } else {
          message.innerHTML = '<strong>Inga produkter hittades</strong><span>Prova ett annat sökord eller välj Alla märken.</span>';
        }
        resultGrid.appendChild(message);
      }

      grid.hidden = true;
      if (pagination && pagination.isConnected) pagination.style.display = 'none';
      document.dispatchEvent(new CustomEvent('pax:products-appended', {
        detail: { count: shown, filtered: true }
      }));
      return shown;
    };

    const applyFilter = async () => {
      if (!discovery) return;

      const revision = ++filterRevision;
      const query = normalize(queryInput && queryInput.value);
      const vendorKey = normalize(selectedVendor);
      const active = Boolean(query || vendorKey);

      if (!active) {
        showNormalGrid();
        return;
      }

      filterActive = true;
      if (clearButton) clearButton.hidden = false;

      const hasMorePages = Boolean(initialNextUrl);
      let items = allKnownItems();
      let shown = renderFilteredGrid(items, query, vendorKey, hasMorePages);

      if (status) {
        status.classList.add('is-loading');
        status.textContent = hasMorePages
          ? (shown ? shown + ' träffar hittills – söker resten…' : 'Söker i hela kollektionen…')
          : shown + ' av ' + items.length + ' produkter visas';
      }

      if (hasMorePages) {
        try {
          await scanAllPages();
        } catch (error) {
          console.error('PAX collection filter:', error);
        }
      }

      if (revision !== filterRevision) return;

      items = allKnownItems();
      shown = renderFilteredGrid(items, query, vendorKey, false);

      if (status) {
        status.classList.remove('is-loading');
        status.textContent = shown + ' av ' + items.length + ' produkter visas';
      }
    };

    if (discovery) {
      if (brandList) {
        brandList.addEventListener('click', (event) => {
          const button = event.target.closest('[data-pax-vendor]');
          if (!button) return;
          selectedVendor = button.dataset.paxVendor || '';
          setBrandActiveState();
          applyFilter();
        });
      }

      if (queryInput) {
        queryInput.addEventListener('input', () => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(applyFilter, 220);
        });
        queryInput.addEventListener('search', applyFilter);
      }

      if (clearButton) {
        clearButton.addEventListener('click', () => {
          clearTimeout(debounceTimer);
          filterRevision += 1;
          if (queryInput) queryInput.value = '';
          selectedVendor = '';
          setBrandActiveState();
          showNormalGrid();
          if (queryInput) queryInput.focus();
        });
      }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
